import { type StorageIntegrationProvider } from "@/schema"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { useApiContext } from "@/hooks/api/use-api-context"

import { deleteStorageIntegration, getStorageAuthUri, updateStorageIntegration } from "./api"
import { storageIntegrationsQueryKeys } from "./queries"

export function useGetAuthUriMutation(bookId?: string) {
  const getContext = useApiContext(bookId)

  return useMutation({
    mutationFn: async (params: {
      provider: StorageIntegrationProvider
      returnUrl?: string
      storageIntegrationId?: string
      egnyteDomain?: string
      context?: string
    }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return getStorageAuthUri(context, params)
    },
  })
}

export function useUpdateStorageIntegrationMutation() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { is_active: boolean } }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return updateStorageIntegration(context, id, data)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storageIntegrationsQueryKeys.all })
    },
  })
}

export function useDeleteStorageIntegrationMutation() {
  const queryClient = useQueryClient()
  const getContext = useApiContext()

  return useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      const context = await getContext()
      if (!context) throw new Error("No context found")
      return deleteStorageIntegration(context, id)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: storageIntegrationsQueryKeys.all })
    },
  })
}
